import { useId, useState } from 'react';
import { describeLoadFailure, jsonOrThrow } from '../apiJson';
import { trackGenerateLead } from '../ga';
import {
  BellIcon,
  SEARCH_ALERT_BENEFITS,
  searchAlertSignupSuccessMessage,
} from '../searchAlertBenefits';
import { FacebookGeneralDealsNudge } from './FacebookGeneralDealsNudge';
import './SearchAlertSignupPanel.css';

/**
 * @param {{
 *   searchPath: string;
 *   scopeLabel: string;
 *   scopeChips?: string[];
 *   variant?: 'empty' | 'end';
 *   showFacebook?: boolean;
 * }} props
 */
export function SearchAlertSignupPanel({
  searchPath,
  scopeLabel,
  scopeChips = [],
  variant = 'empty',
  showFacebook = true,
}) {
  const emailId = useId();
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [submitted, setSubmitted] = useState(false);

  const heading =
    typeof scopeLabel === 'string' && scopeLabel.trim() !== ''
      ? scopeLabel.trim()
      : 'this search';

  const chips = Array.isArray(scopeChips)
    ? scopeChips.filter((c) => typeof c === 'string' && c.trim() !== '')
    : [];

  const facebookLocation =
    variant === 'end' ? 'search_alert_panel_end' : 'search_alert_panel_empty';

  const title =
    variant === 'end'
      ? "You've seen everything for now"
      : 'Nothing here yet';

  const lede =
    variant === 'end'
      ? `Want to know when more products appear for ${heading}?`
      : `We couldn't find products for ${heading} right now.`;

  async function handleSubmit(e) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const res = await fetch('/api/search-alert-signup.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email.trim(),
          searchPath,
          scopeLabel: heading,
        }),
      });
      await jsonOrThrow(res);
      setSubmitted(true);
      trackGenerateLead('search_alert');
    } catch (err) {
      setError(describeLoadFailure(err));
    } finally {
      setSubmitting(false);
    }
  }

  const emailSection = submitted ? (
    <p
      className="search-alert-panel__success search-alert-panel__email-success"
      role="status"
    >
      {searchAlertSignupSuccessMessage(heading)}
    </p>
  ) : (
    <div className="search-alert-panel__email-column">
      <p className="search-alert-panel__email-label">Get email alerts</p>
      <ul className="search-alert-panel__benefits">
        {SEARCH_ALERT_BENEFITS.map(({ text, Icon }) => (
          <li key={text} className="search-alert-panel__benefit">
            <span className="search-alert-panel__benefit-icon" aria-hidden="true">
              <Icon />
            </span>
            <span>{text}</span>
          </li>
        ))}
      </ul>

      {error !== null && (
        <p className="search-alert-panel__error" role="alert">
          {error}
        </p>
      )}

      <form
        className="search-alert-panel__form"
        onSubmit={handleSubmit}
        noValidate
      >
        <label className="sr-only" htmlFor={emailId}>
          Email for search alerts
        </label>
        <input
          id={emailId}
          name="email"
          className="search-alert-panel__input"
          type="email"
          autoComplete="email"
          inputMode="email"
          placeholder="you@example.com"
          value={email}
          onChange={(ev) => setEmail(ev.target.value)}
          required
          maxLength={255}
          disabled={submitting}
        />
        <button
          type="submit"
          className="search-alert-panel__submit search-alert-panel__submit--secondary"
          disabled={submitting}
        >
          {submitting ? 'Joining…' : 'Get email alerts'}
        </button>
      </form>
    </div>
  );

  return (
    <aside
      className={`search-alert-panel search-alert-panel--${variant}`}
      aria-labelledby={`${emailId}-title`}
    >
      <div className="search-alert-panel__icon" aria-hidden="true">
        <BellIcon />
      </div>
      <div className="search-alert-panel__content">
        <h2 id={`${emailId}-title`} className="search-alert-panel__title">
          {title}
        </h2>
        <p className="search-alert-panel__lede">{lede}</p>

        {chips.length > 0 && (
          <ul className="search-alert-panel__chips" aria-label="Alert scope">
            {chips.map((chip) => (
              <li key={chip} className="search-alert-panel__chip">
                {chip}
              </li>
            ))}
          </ul>
        )}

        <div
          className={
            showFacebook
              ? 'search-alert-panel__columns'
              : 'search-alert-panel__columns search-alert-panel__columns--email-only'
          }
        >
          {showFacebook && (
            <FacebookGeneralDealsNudge location={facebookLocation} />
          )}
          {emailSection}
        </div>
      </div>
    </aside>
  );
}
